import React, { useContext, useCallback, useRef, useLayoutEffect } from "react"
import { useDispatch } from "react-redux"
import { PlayCircleFill } from "react-bootstrap-icons"
import { ActionCableConsumer } from "react-actioncable-provider"
import { useQueryClient } from "react-query"
import { UserContext } from "../HomeApp"
import { useHotBits } from "../../hooks/useHotBits"
import { prependAndPlay } from "../../stores/mediaPlayerSlice"
import HotBitsSong from "./HotBitsSong"
import HotWaxAlbum from "./HotWaxAlbum"
import HotListsPlaylist from "./HotListsPlaylist"
import styles from "./styles.module.css"

const HotBits = () => {
  const { scene } = useContext(UserContext)
  const { data, isLoading, isError } = useHotBits(scene)
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const scrollTrackRef = useRef<HTMLDivElement>(null)
  const hotListsPanelRef = useRef<HTMLDivElement>(null)
  const hotBitsPanelRef = useRef<HTMLDivElement>(null)
  const hotWaxPanelRef = useRef<HTMLDivElement>(null)
  const didInitialScroll = useRef(false)

  const handleReceived = useCallback(() => {
    queryClient.invalidateQueries(["song_stats_days", scene])
  }, [queryClient, scene])

  // Snap to Hot Bits on first render with data, before paint
  useLayoutEffect(() => {
    if (
      didInitialScroll.current ||
      !data ||
      !scrollTrackRef.current ||
      !hotBitsPanelRef.current
    )
      return
    scrollTrackRef.current.scrollLeft = hotBitsPanelRef.current.offsetLeft
    didInitialScroll.current = true
  }, [data])

  if (isLoading || isError) return null
  if (!data?.enabled || !data?.songs?.length) return null

  const songs = data.songs.slice(0, 10)
  const hotBitsAttrs = {
    playlistId: data.hot_bits_playlist_id ?? null,
    playlist_name: "Hot Bits",
    playlist_path: data.hot_bits_playlist_path ?? null,
  }
  const hotWaxAttrs = {
    playlistId: data.hot_wax_playlist_id ?? null,
    playlist_name: "Hot Wax",
    playlist_path: data.hot_wax_playlist_path ?? null,
  }
  const hotListsAttrs = {
    playlistId: data.hot_lists_playlist_id ?? null,
    playlist_name: "Hot Lists",
    playlist_path: data.hot_lists_playlist_path ?? null,
  }

  const scrollToPanel = (ref: React.RefObject<HTMLDivElement>) => {
    if (scrollTrackRef.current && ref.current) {
      scrollTrackRef.current.scrollTo({
        left: ref.current.offsetLeft,
        behavior: "smooth",
      })
    }
  }

  const onPlayAll = () => {
    dispatch(
      prependAndPlay(
        songs.map((song) => ({
          id: song.post_id,
          title: song.title,
          band_id: song.band_id,
          band_name: song.band_name,
          album_id: song.album_id,
          album_name: song.album_name,
          album_path: song.album_path,
          cover_image_small: song.cover_image_small,
          duration: song.duration,
          local_path: song.local_path,
          nada_path: song.nada_path,
          parent_path: song.nada_path,
          streaming_url: song.streaming_url,
          post_type: song.post_type,
          ...hotBitsAttrs,
        })),
      ),
    )
  }

  return (
    <div className={styles.scrollOuter}>
      <div className={styles.scrollTrack} ref={scrollTrackRef}>
        {/* Hot Lists */}
        <div
          ref={hotListsPanelRef}
          className={styles.panel}
          style={{ backgroundImage: `url(${data.hot_lists_background_image})` }}
        >
          <div className={styles.header}>
            <span />
            <button
              className={styles.navLink}
              onClick={() => scrollToPanel(hotBitsPanelRef)}
            >
              HOT BITS
            </button>
          </div>
          <div className={styles.playAllRow}>
            <button
              onClick={() => {
                const allSongs = (data.playlists ?? []).flatMap((pl: any) =>
                  pl.songs.map((song: any) => ({
                    id: song.post_id,
                    title: song.title,
                    band_id: song.band_id,
                    band_name: song.band_name,
                    album_id: song.album_id,
                    album_name: song.album_name,
                    album_path: song.album_path,
                    cover_image_small: song.cover_image_small,
                    duration: song.duration,
                    local_path: song.local_path,
                    nada_path: song.nada_path,
                    parent_path: song.nada_path,
                    streaming_url: song.streaming_url,
                    post_type: song.post_type,
                    ...hotListsAttrs,
                  })),
                )
                dispatch(prependAndPlay(allSongs))
              }}
              aria-label="Play all Hot Lists"
              className={styles.playAllButton}
            >
              <PlayCircleFill size={60} />
            </button>
          </div>
          <div className={styles.columnLabels}>
            <span className={styles.columnLabelRank}>#</span>
            <span>
              <span className={styles.columnLabelTitleWord}>PLAYLIST / </span>
              <span className={styles.columnLabelArtistWord}>CURATOR</span>
            </span>
          </div>
          <div className={styles.divider} />
          {data.playlists?.map((pl: any, i: number) => (
            <HotListsPlaylist
              key={pl.playlist_id}
              playlist={pl}
              rank={i + 1}
              hotListsAttrs={hotListsAttrs}
            />
          ))}
        </div>

        {/* Hot Bits */}
        <div
          ref={hotBitsPanelRef}
          className={styles.panel}
          style={{ backgroundImage: `url(${data.hot_bits_background_image})` }}
        >
          <ActionCableConsumer
            channel={{ channel: "HotBitsChannel", scene }}
            onReceived={handleReceived}
          />
          <div className={styles.header}>
            <button
              className={styles.navLink}
              onClick={() => scrollToPanel(hotListsPanelRef)}
            >
              HOT LISTS
            </button>
            <button
              className={styles.navLink}
              onClick={() => scrollToPanel(hotWaxPanelRef)}
            >
              HOT WAX
            </button>
          </div>
          <div className={styles.playAllRow}>
            <button
              onClick={onPlayAll}
              aria-label="Play all Hot Bits"
              className={styles.playAllButton}
            >
              <PlayCircleFill size={60} />
            </button>
          </div>
          <div className={styles.columnLabels}>
            <span className={styles.columnLabelRank}>#</span>
            <span>
              <span className={styles.columnLabelTitleWord}>TITLE / </span>
              <span className={styles.columnLabelArtistWord}>ARTIST</span>
            </span>
          </div>
          <div className={styles.divider} />
          {songs.map((song, i) => (
            <HotBitsSong
              key={song.post_id}
              song={song}
              rank={i + 1}
              hotBitsAttrs={hotBitsAttrs}
            />
          ))}
        </div>

        {/* Hot Wax */}
        <div
          ref={hotWaxPanelRef}
          className={styles.panel}
          style={{ backgroundImage: `url(${data.hot_wax_background_image})` }}
        >
          <div className={styles.header}>
            <button
              className={styles.navLink}
              onClick={() => scrollToPanel(hotBitsPanelRef)}
            >
              HOT BITS
            </button>
            <span />
          </div>
          <div className={styles.playAllRow}>
            <button
              onClick={() => {
                const allSongs = (data.albums ?? []).flatMap((album: any) =>
                  album.songs.map((song: any) => ({
                    id: song.post_id,
                    title: song.title,
                    band_id: song.band_id,
                    band_name: song.band_name,
                    album_id: song.album_id,
                    album_name: song.album_name,
                    album_path: song.album_path,
                    cover_image_small: song.cover_image_small,
                    duration: song.duration,
                    local_path: song.local_path,
                    nada_path: song.nada_path,
                    parent_path: song.nada_path,
                    streaming_url: song.streaming_url,
                    post_type: song.post_type,
                    ...hotWaxAttrs,
                  })),
                )
                dispatch(prependAndPlay(allSongs))
              }}
              aria-label="Play all Hot Wax"
              className={styles.playAllButton}
            >
              <PlayCircleFill size={60} />
            </button>
          </div>
          <div className={styles.columnLabels}>
            <span className={styles.columnLabelRank}>#</span>
            <span>
              <span className={styles.columnLabelTitleWord}>ALBUM / </span>
              <span className={styles.columnLabelArtistWord}>ARTIST</span>
            </span>
          </div>
          <div className={styles.divider} />
          {data.albums?.map((album: any, i: number) => (
            <HotWaxAlbum
              key={album.album_id}
              album={album}
              rank={i + 1}
              hotWaxAttrs={hotWaxAttrs}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default HotBits
